import { resolvePrimaryColor } from 'src/utils/workspace-logo'
import {
  DEFAULT_COVER_VARIANTS,
  coverArtBackground,
  coverArtUrl,
} from 'src/utils/default-cover-art'

const DEFAULT_PRIMARY = '#015FFB'

/**
 * The Default preset's header image and the images shown when a course or
 * path has no cover. New courses, paths and portal pages are created with
 * them; they are drawn in the page's primary color instead, as in the learner
 * app (utils/defaultHeaderBackground there).
 */
export const isDefaultHeaderImage = url =>
  !!url &&
  /\/(landing-page-templates\/(header|thumbnails)\/default|gateway\/(collection|course)-cover)\.png(\?|#|$)/i.test(url)

const colorOf = primaryColor => {
  // A page on the default color shows the workspace branding's.
  const resolved = resolvePrimaryColor(primaryColor)
  return /^#[0-9a-f]{3,8}$/i.test(String(resolved || '').trim())
    ? String(resolved).trim()
    : DEFAULT_PRIMARY
}

/** CSS `background` of a header: the "Açık demet" hero art in the color. */
export const primaryHeaderBackground = primaryColor => {
  const color = colorOf(primaryColor)
  return `url(${coverArtUrl(color, 0, 'hero')}) center / cover no-repeat, ${coverArtBackground(
    color,
    'hero',
  )}`
}

/** CSS `background` of a card without a cover: art `variant` (0–4) in the color. */
export const defaultThumbBackground = (primaryColor, variant = 0, aspect) => {
  const color = colorOf(primaryColor)
  return `url(${coverArtUrl(
    color,
    variant % DEFAULT_COVER_VARIANTS,
    'thumb',
    aspect,
  )}) center / cover no-repeat, ${coverArtBackground(color)}`
}

/**
 * CSS `background` for a landing page header: the Default preset's image
 * becomes the primary-color art; any other image keeps its darkening overlay.
 */
export const headerBackgroundCss = (imageUrl, overlay, primaryColor) =>
  isDefaultHeaderImage(imageUrl)
    ? primaryHeaderBackground(primaryColor)
    : `linear-gradient(0deg, rgba(0, 0, 0, ${overlay}) 0%, rgba(0, 0, 0, ${overlay}) 100%), url(${imageUrl}) lightgray 50% / cover no-repeat`

const DEFAULT_COVER_FILES = [
  'landing-page-templates/header/Default.png',
  'landing-page-templates/thumbnails/Default.png',
  'gateway/collection-cover.png',
  'gateway/course-cover.png',
]

/** Every <img> or inline background still showing a default cover image. */
export const DEFAULT_COVER_SELECTOR = DEFAULT_COVER_FILES.flatMap(file => [
  `img[src*="/assets/${file}" i]`,
  `[style*="/assets/${file}" i]`,
]).join(', ')

/**
 * A stylesheet that draws every default cover in `background` until the
 * default cover script gives each card its own art (the image is pushed out
 * of its box with object-position, so layout and sizing stay the same).
 */
export const defaultCoverCss = background => {
  const images = DEFAULT_COVER_FILES.map(
    file => `img[src*="/assets/${file}" i]`,
  ).join(', ')
  const backgrounds = DEFAULT_COVER_FILES.map(
    file => `[style*="/assets/${file}" i]`,
  ).join(', ')
  return `${images} { object-position: -99999px -99999px !important; background: ${background} !important; }
${backgrounds} { background: ${background} !important; }`
}
